import {
  Constants,
  SubmittalDoc,
  SubmittalRevision,
  WorkflowState,
  WorkflowStateComplete,
  SubmittalFeedItemDoc,
  SubmittalMarkupDoc,
  SubmittalSettingsDoc,
  FeedItemSubType,
  DateUtils,
  Calendar,
  CommonSubmittalUtils,
  MarkupCommonUtils,
  MarkupType,
  Workflow,
} from 'common';
import { isUndefined } from 'lodash';
import { ConcourseDb } from 'src/global';

/**
 * Parsing, sequencing, and formatting of submittal identifier numbers.
 */
export class SubmittalUtils extends CommonSubmittalUtils {
  static getWriteRoles(projectId: string, submittalId: string): string[] {
    return [`p-${projectId}-submittals-${submittalId}-write`, `p-${projectId}-submittals-write`, `p-${projectId}-write`];
  }

  static getReadRoles(projectId: string, submittalId: string): string[] {
    return [...this.getWriteRoles(projectId, submittalId), `p-${projectId}-submittals-${submittalId}`, `p-${projectId}-submittals`, `p-${projectId}`];
  }

  static sortWorkflowsByUpdatedDate(workflows: Workflow[] = []): Workflow[] {
    return [...workflows].sort((a, b) => (a.updated_date || '').localeCompare(b.updated_date || ''));
  }

  static getDefaultWorkflow(settings?: SubmittalSettingsDoc): Workflow {
    const workflows = [...(settings?.workflows || [])];
    if (!workflows.some(w => w.id === this.DEFAULT_SUBMITTAL_WORKFLOW.id)) {
      workflows.unshift(this.DEFAULT_SUBMITTAL_WORKFLOW);
    }
    const sorted = this.sortWorkflowsByUpdatedDate(workflows);
    return sorted[sorted.length - 1];
  }

  static createDraftSubmittal(userId: string, code: string, workflow: Workflow, calendar: Calendar): SubmittalDoc {
    const dueDays = workflow.states.reduce((sum, st) => sum + st.dueDays, 0);
    const dueDate = new Date(DateUtils.currentDate.getTime() + dueDays * DateUtils.MILLIS_PER_DAY).toISOString();
    return {
      _id: ConcourseDb.makeShortDocId(),
      code: code,
      spec_section: null,
      cost_code: null,
      title: null,
      type: null,
      package: null,
      description: null,
      due_date: dueDate,
      distribution: [],
      predecessors: [],
      successors: [],
      tags: [],
      revisions: [
        {
          num: 0,
          states: [...workflow.states],
          created_by: userId,
          created_date: DateUtils.currentIso,
        },
      ],
    };
  }

  /**
   * Searches the submittals for the max number and then returns its components.
   * @param numbers Array of submittal number strings to search
   * @returns object with prefix, num, and length, e.g SUB-042 becomes { prefix: 'SUB-', num: 42, length: 3 }
   * if no numbers passed or none contained numbers, returns as if we passed 000 { prefix: '', num: 0, length: 3 }
   */
  static getMaxPattern(numbers: string[]): { prefix: string; num: number; length: number } {
    return numbers.reduce(
      (best, number) => {
        const match = number && number.match(/^(?:(.*)-)?(\d+)$/); // only match trailing digits
        if (!match) return best; // skip invalid

        const cur = { prefix: match[1] ?? '', num: parseInt(match[2], 10), length: match[2].length };

        if (!best) return cur;
        if (cur.num !== best.num) return cur.num > best.num ? cur : best;
        return cur.length > best.length ? cur : best; // tie-breaker
      },
      { prefix: '', num: 0, length: 3 } as { prefix: string; num: number; length: number },
    );
  }

  static getNextNumber(numbers: string[]): string {
    const max = this.getMaxPattern(numbers);
    return this.getNumberFromPattern(max.prefix, max.num + 1, max.length);
  }

  static getNumberFromPattern(prefix: string, num: number, padding: number): string {
    return `${prefix}${String(num).padStart(padding, '0')}`;
  }

  static getRevisionPendingStates(revision: SubmittalRevision): WorkflowState[] {
    return isUndefined(revision.completed_state_index) ? revision.states : revision.states.slice(revision.completed_state_index + 1);
  }

  static feedToStatusHistory(feed: SubmittalFeedItemDoc[]): WorkflowStateComplete[] {
    return feed.map(item => ({
      state_id: item.sub_id,
      user_id: item.user_id,
      date: item.date,
    }));
  }

  static renderSubmittalName(doc?: SubmittalDoc, chars?: number, fallback?: string): string {
    let fullName = '';
    if (!doc && !fallback) {
      return '';
    }

    if (!doc) {
      fullName = fallback;
    } else {
      fullName = ` #${doc.code} - ${doc.title}`;
    }

    if (chars && fullName.length > chars) {
      return `${fullName.substring(0, chars)}...`;
    }

    return fullName;
  }

  static getExcludedFileLikeFeedItems(feed: SubmittalFeedItemDoc[]): SubmittalFeedItemDoc[] {
    return feed.filter(
      item =>
        item.excluded &&
        !item.deleted_date &&
        !item.deleted_by &&
        (item.sub === FeedItemSubType.FILE || item.sub === FeedItemSubType.PHOTO),
    );
  }

  /** Shared referenceId for persisted citation highlight SHAPE markups. */
  static readonly SPEC_CITATION_REFERENCE_ID = 'spec-citation';

  static escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  static findSpecCitationMarkups(markups: SubmittalMarkupDoc[], fileId: string, pageId: string): SubmittalMarkupDoc[] {
    return (markups || []).filter(
      m => m.referenceId === SubmittalUtils.SPEC_CITATION_REFERENCE_ID && m.fileId === fileId && m.pageId === pageId,
    );
  }

  static hasMatchingSpecCitationMarkups(
    markups: SubmittalMarkupDoc[],
    fileId: string,
    pageId: string,
    citation: string,
  ): boolean {
    return SubmittalUtils.findSpecCitationMarkups(markups, fileId, pageId).some(m => m.content === citation);
  }

  /** One SHAPE per PctRect in markup page space (880-wide). */
  static createSpecCitationShapes(
    createdBy: string,
    createdDate: string,
    revision: number,
    fileId: string,
    pageId: string,
    citation: string,
    rects: Array<{ t: number; r: number; b: number; l: number }>,
    thumbWidth: number,
    thumbHeight: number,
  ): SubmittalMarkupDoc[] {
    const pageWidth = MarkupCommonUtils.MARKUP_PAGE_WIDTH;
    const pageHeight = thumbHeight * (pageWidth / thumbWidth);
    return rects.map(rect => {
      const markup = MarkupCommonUtils.createMarkup(createdBy, {
        fileId,
        pageId,
        type: MarkupType.SHAPE,
        x: rect.l * pageWidth,
        y: rect.t * pageHeight,
        width: (rect.r - rect.l) * pageWidth,
        height: (rect.b - rect.t) * pageHeight,
        content: citation,
        referenceId: SubmittalUtils.SPEC_CITATION_REFERENCE_ID,
        style: {
          fill: 'rgba(255, 0, 0, 0.35)',
          stroke: 'none',
          strokeWidth: 0,
        },
      });
      return {
        ...markup,
        created_by: createdBy,
        created_date: createdDate,
        revision,
      };
    });
  }
}
